import React, { Component } from 'react';
import Spinner from '../common/Spinner';
import { connect } from 'react-redux';
import { withRouter } from '../../utils/withRouter';
import { getUsers, updateUser, getUser, deleteUser, getUserFilterValues, changeUserProgram, changeUserPath, bulkDeactivateUsers, bulkActivateUsers, bulkDeleteUsers, bulkChangeProgram, bulkChangePath, adjustUserXP, bulkAdjustXP, bulkResetProgress } from '../../actions/userActions';
import { getBadges, awardBadge } from '../../actions/badgeActions';
import { startImpersonation, resetUserProgress } from '../../actions/authActions';
import { getPrograms } from '../../actions/programActions';
import PropTypes from 'prop-types';
import { Filter, X, UserCheck, RotateCcw, UserRound, ChevronLeft, ChevronRight, ChevronDown, Calendar, ArrowRightLeft, GitBranch, MoreVertical, Eye, Pencil, Trash2, Award, Zap, ArrowUpDown } from 'lucide-react';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';

class ManageUsersTab extends Component {
  static propTypes = {
    getUsers: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool
  };

  state = {
    roleFilter: null, // Single role filter
    statusFilter: 'all', // 'all', 'active', or 'deactivated'
    showRoleDropdown: false,
    showStatusDropdown: false,
    isModalOpen: false,
    selectedUser: null,
    modalMode: 'view', // 'view' or 'edit'
    // Modal form state
    name: '',
    lastName: '',
    email: '',
    country: '',
    birthYear: '',
    role: '',
    isActive: true,
    showSuccess: false,
    // Reset progress modal
    showResetModal: false,
    resetUser: null,
    isResetting: false,
    resetMessage: { type: '', text: '' },
    // Delete user modal
    showDeleteModal: false,
    deleteUser: null,
    isDeleting: false,
    deleteMessage: { type: '', text: '' },
    // Change Program modal
    showChangeProgramModal: false,
    changeProgramUser: null,
    selectedGroupId: '',
    isChangingProgram: false,
    changeProgramMessage: { type: '', text: '' },
    // Change Pathway modal
    showChangePathModal: false,
    changePathUser: null,
    selectedPathId: '',
    isChangingPath: false,
    changePathMessage: { type: '', text: '' },
    // Action dropdown
    openActionDropdown: null,
    dropdownPosition: { top: 0, left: 0 },
    // Bulk selection state
    selectedUserIds: [],
    showBulkDeactivateModal: false,
    isBulkDeactivating: false,
    bulkDeactivateMessage: { type: '', text: '' },
    showBulkActivateModal: false,
    isBulkActivating: false,
    bulkActivateMessage: { type: '', text: '' },
    showBulkDeleteModal: false,
    isBulkDeleting: false,
    bulkDeleteMessage: { type: '', text: '' },
    showBulkChangeProgramModal: false,
    isBulkChangingProgram: false,
    bulkChangeProgramMessage: { type: '', text: '' },
    bulkSelectedGroupId: '',
    showBulkChangePathModal: false,
    isBulkChangingPath: false,
    bulkChangePathMessage: { type: '', text: '' },
    bulkSelectedPathId: '',
    showBulkActionDropdown: false,
    // Adjust XP modal
    showAdjustXPModal: false,
    adjustXPUser: null,
    adjustXPAmount: '',
    adjustXPOperation: 'add',
    isAdjustingXP: false,
    adjustXPMessage: { type: '', text: '' },
    // Bulk Reset Progress modal
    showBulkResetProgressModal: false,
    isBulkResettingProgress: false,
    bulkResetProgressMessage: { type: '', text: '' },

    // Bulk Adjust XP modal
    showBulkAdjustXPModal: false,
    bulkAdjustXPAmount: '',
    bulkAdjustXPOperation: 'add',
    isBulkAdjustingXP: false,
    bulkAdjustXPMessage: { type: '', text: '' },
    // Impersonate modal
    showImpersonateModal: false,
    impersonateUser: null,
    // Award Badge modal
    showAwardBadgeModal: false,
    awardBadgeUser: null,
    selectedBadgeId: '',
    isAwardingBadge: false,
    awardBadgeMessage: { type: '', text: '' },
    // Drag to scroll state
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    // Pagination
    currentPage: 1,
    limit: 50,
    // Filter state
    selectedNames: [],
    selectedEmails: [],
    selectedCountries: [],
    selectedUniversities: [],
    selectedGroups: [],
    startDate: '',
    endDate: '',
    showFilters: false,
    sortOrder: 'desc', // 'desc' = newest first, 'asc' = oldest first
    // Filter values from API
    filterValues: {
      names: [],
      emails: [],
      countries: [],
      universities: [],
      groups: []
    }
  };

  tableRef = React.createRef();

  componentDidMount() {
    this.fetchUsers();
    this.fetchFilterValues();
  }

  fetchFilterValues = () => {
    // Fetch all filter values (not filtered by status)
    this.props.getUserFilterValues().then(filterValues => {
      this.setState({ filterValues });
    });
  };

  fetchUsers = () => {
    const { statusFilter, roleFilter, currentPage, limit, selectedNames, selectedEmails, selectedCountries, selectedUniversities, selectedGroups, startDate, endDate, sortOrder } = this.state;
    
    const filters = {};
    
    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }
    
    if (roleFilter) {
      filters.role = roleFilter;
    }
    
    if (selectedNames.length > 0) {
      filters.names = selectedNames;
    }
    
    if (selectedEmails.length > 0) {
      filters.emails = selectedEmails;
    }
    
    if (selectedCountries.length > 0) {
      filters.countries = selectedCountries;
    }

    if (selectedUniversities.length > 0) {
      filters.universities = selectedUniversities;
    }

    if (selectedGroups.length > 0) {
      filters.groups = selectedGroups;
    }
    
    if (startDate) {
      filters.startDate = startDate;
    }
    
    if (endDate) {
      filters.endDate = endDate;
    }
    
    const options = {
      page: currentPage,
      limit,
      sortBy: 'createdAt',
      sortOrder
    };
    
    this.props.getUsers(filters, options);
  };

  handlePageChange = (newPage) => {
    this.setState({ currentPage: newPage, selectedUserIds: [] }, () => {
      this.fetchUsers();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  handleFilterChange = () => {
    this.setState({ currentPage: 1, selectedUserIds: [] }, () => {
      this.fetchUsers();
    });
  };

  toggleFilters = () => {
    this.setState(prevState => ({ showFilters: !prevState.showFilters }));
  };

  clearAllFilters = () => {
    this.setState({
      statusFilter: 'all',
      roleFilter: null,
      selectedNames: [],
      selectedEmails: [],
      selectedCountries: [],
      selectedUniversities: [],
      selectedGroups: [],
      startDate: '',
      endDate: '',
      sortOrder: 'desc',
      currentPage: 1
    }, () => {
      this.fetchUsers();
    });
  };

  handleMouseDown = (e) => {
    // Ignore if clicking on the actions column (last column with buttons)
    const actionsCell = e.target.closest('td:last-child, th:last-child');
    if (actionsCell) {
      return;
    }
    
    const table = this.tableRef.current;
    if (!table) return;
    
    this.setState({
      isDragging: true,
      startX: e.pageX - table.offsetLeft,
      scrollLeft: table.scrollLeft
    });
    table.style.cursor = 'grabbing';
    table.style.userSelect = 'none';
  };

  handleMouseLeave = () => {
    if (this.state.isDragging) {
      this.setState({ isDragging: false });
      if (this.tableRef.current) {
        this.tableRef.current.style.cursor = 'grab';
        this.tableRef.current.style.userSelect = 'auto';
      }
    }
  };

  handleMouseUp = () => {
    this.setState({ isDragging: false });
    if (this.tableRef.current) {
      this.tableRef.current.style.cursor = 'grab';
      this.tableRef.current.style.userSelect = 'auto';
    }
  };

  handleMouseMove = (e) => {
    if (!this.state.isDragging) return;
    e.preventDefault();
    
    const table = this.tableRef.current;
    if (!table) return;
    
    const x = e.pageX - table.offsetLeft;
    const walk = (x - this.state.startX) * 2; // Scroll speed multiplier
    table.scrollLeft = this.state.scrollLeft - walk;
  };

  toggleRoleDropdown = () => {
    this.setState(prevState => ({ showRoleDropdown: !prevState.showRoleDropdown }));
  };

  handleRoleSelection = (role) => {
    this.setState({ 
      roleFilter: role === 'all' ? null : role,
      showRoleDropdown: false,
      currentPage: 1
    }, () => {
      this.fetchUsers();
    });
  };

  handleStatusSelection = (status) => {
    this.setState({ 
      statusFilter: status,
      currentPage: 1,
      showStatusDropdown: false
    }, () => {
      this.fetchUsers();
    });
  };

  toggleStatusDropdown = () => {
    this.setState(prevState => ({ showStatusDropdown: !prevState.showStatusDropdown }));
  };

  handleViewClick = (user) => {
    this.setUserData(user);
    this.setState({
      selectedUser: user,
      isModalOpen: true,
      modalMode: 'view'
    });
  };

  handleViewProfileClick = (user) => {
    // Navigate to profile page with user ID as query parameter
    this.props.navigate(`/profile?id=${user._id}`);
  };

  handleEditClick = (user) => {
    this.setUserData(user);
    this.setState({
      selectedUser: user,
      isModalOpen: true,
      modalMode: 'edit'
    });
  };

  handleImpersonateClick = (user) => {
    this.setState({ showImpersonateModal: true, impersonateUser: user });
  };

  handleConfirmImpersonate = () => {
    if (this.state.impersonateUser) {
      this.props.startImpersonation(this.state.impersonateUser._id);
    }
    this.setState({ showImpersonateModal: false, impersonateUser: null });
  };

  handleCloseImpersonateModal = () => {
    this.setState({ showImpersonateModal: false, impersonateUser: null });
  };

  handleDeleteClick = (user) => {
    this.setState({
      showDeleteModal: true,
      deleteUser: user,
      deleteMessage: { type: '', text: '' }
    });
  };

  handleResetProgressClick = (user) => {
    this.setState({
      showResetModal: true,
      resetUser: user,
      resetMessage: { type: '', text: '' }
    });
  };

  handleConfirmResetProgress = () => {
    if (!this.state.resetUser) return;
    
    this.setState({ isResetting: true });
    this.props.resetUserProgress(this.state.resetUser._id)
      .then(() => {
        this.setState({
          isResetting: false,
          resetMessage: {
            type: 'success',
            text: 'User progress and XP points reset successfully!'
          }
        });
      })
      .catch(() => {
        this.setState({
          isResetting: false,
          resetMessage: {
            type: 'error',
            text: 'Failed to reset user progress and XP points. Please try again.'
          }
        });
      });
  };

  handleCloseResetModal = () => {
    this.setState({
      showResetModal: false,
      resetUser: null,
      resetMessage: { type: '', text: '' },
      isResetting: false
    });
  };

  handleConfirmDeleteUser = () => {
    if (!this.state.deleteUser) return;
    
    this.setState({ isDeleting: true });
    this.props.deleteUser(this.state.deleteUser._id)
      .then(() => {
        this.setState({
          isDeleting: false,
          deleteMessage: {
            type: 'success',
            text: 'User deleted successfully!'
          }
        });
        // Auto close after 2 seconds
        setTimeout(() => {
          this.setState({
            showDeleteModal: false,
            deleteUser: null,
            deleteMessage: { type: '', text: '' }
          });
        }, 2000);
      })
      .catch(() => {
        this.setState({
          isDeleting: false,
          deleteMessage: {
            type: 'error',
            text: 'Failed to delete user. Please try again.'
          }
        });
      });
  };

  handleCloseDeleteModal = () => {
    this.setState({
      showDeleteModal: false,
      deleteUser: null,
      deleteMessage: { type: '', text: '' },
      isDeleting: false
    });
  };

  // Change Program handlers
  handleChangeProgramClick = (user) => {
    // Load programs if not already loaded
    if (!this.props.programs || this.props.programs.length === 0) {
      this.props.getPrograms();
    }
    this.setState({
      showChangeProgramModal: true,
      changeProgramUser: user,
      selectedGroupId: '',
      selectedPathId: '',
      changeProgramMessage: { type: '', text: '' }
    });
  };

  handleConfirmChangeProgram = () => {
    if (!this.state.changeProgramUser || !this.state.selectedGroupId) return;

    this.setState({ isChangingProgram: true });
    this.props.changeUserProgram(this.state.changeProgramUser._id, this.state.selectedGroupId, this.state.selectedPathId || null)
      .then((data) => {
        this.setState({
          isChangingProgram: false,
          changeProgramMessage: { type: 'success', text: data.msg }
        });
        this.fetchUsers();
      })
      .catch((err) => {
        this.setState({
          isChangingProgram: false,
          changeProgramMessage: { type: 'error', text: err?.msg || 'Failed to change program. Please try again.' }
        });
      });
  };

  handleCloseChangeProgramModal = () => {
    this.setState({
      showChangeProgramModal: false,
      changeProgramUser: null,
      selectedGroupId: '',
      selectedPathId: '',
      changeProgramMessage: { type: '', text: '' },
      isChangingProgram: false
    });
  };

  // Change Pathway handlers
  handleChangePathClick = (user) => {
    if (!this.props.programs || this.props.programs.length === 0) {
      this.props.getPrograms();
    }
    this.setState({
      showChangePathModal: true,
      changePathUser: user,
      selectedPathId: '',
      changePathMessage: { type: '', text: '' }
    });
  };

  handleConfirmChangePath = () => {
    if (!this.state.changePathUser || !this.state.selectedPathId) return;

    this.setState({ isChangingPath: true });
    this.props.changeUserPath(this.state.changePathUser._id, this.state.selectedPathId)
      .then((data) => {
        this.setState({
          isChangingPath: false,
          changePathMessage: { type: 'success', text: data.msg }
        });
        this.fetchUsers();
      })
      .catch((err) => {
        this.setState({
          isChangingPath: false,
          changePathMessage: { type: 'error', text: err?.msg || 'Failed to change pathway. Please try again.' }
        });
      });
  };

  handleCloseChangePathModal = () => {
    this.setState({
      showChangePathModal: false,
      changePathUser: null,
      selectedPathId: '',
      changePathMessage: { type: '', text: '' },
      isChangingPath: false
    });
  };

  // Bulk selection handlers
  handleSelectUser = (userId) => {
    this.setState(prevState => {
      const isSelected = prevState.selectedUserIds.includes(userId);
      return {
        selectedUserIds: isSelected
          ? prevState.selectedUserIds.filter(id => id !== userId)
          : [...prevState.selectedUserIds, userId]
      };
    });
  };

  handleSelectAll = () => {
    const { users } = this.props.user;
    const selectableUsers = (users || []).filter(u => u.role !== 'platform_admin' && (u.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && u._id !== this.props.currentUser?._id);
    const allSelected = selectableUsers.length > 0 && selectableUsers.every(u => this.state.selectedUserIds.includes(u._id));

    if (allSelected) {
      this.setState({ selectedUserIds: [] });
    } else {
      this.setState({ selectedUserIds: selectableUsers.map(u => u._id) });
    }
  };

  handleBulkDeactivateClick = () => {
    this.setState({
      showBulkDeactivateModal: true,
      bulkDeactivateMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkDeactivate = () => {
    this.setState({ isBulkDeactivating: true });
    this.props.bulkDeactivateUsers(this.state.selectedUserIds)
      .then((data) => {
        this.setState({
          isBulkDeactivating: false,
          bulkDeactivateMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
        this.fetchUsers();
      })
      .catch(() => {
        this.setState({
          isBulkDeactivating: false,
          bulkDeactivateMessage: { type: 'error', text: 'Failed to deactivate users. Please try again.' }
        });
      });
  };

  handleCloseBulkDeactivateModal = () => {
    this.setState({
      showBulkDeactivateModal: false,
      bulkDeactivateMessage: { type: '', text: '' },
      isBulkDeactivating: false
    });
  };

  handleBulkActivateClick = () => {
    this.setState({
      showBulkActivateModal: true,
      bulkActivateMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkActivate = () => {
    this.setState({ isBulkActivating: true });
    this.props.bulkActivateUsers(this.state.selectedUserIds)
      .then((data) => {
        this.setState({
          isBulkActivating: false,
          bulkActivateMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
        this.fetchUsers();
      })
      .catch(() => {
        this.setState({
          isBulkActivating: false,
          bulkActivateMessage: { type: 'error', text: 'Failed to activate users. Please try again.' }
        });
      });
  };

  handleCloseBulkActivateModal = () => {
    this.setState({
      showBulkActivateModal: false,
      bulkActivateMessage: { type: '', text: '' },
      isBulkActivating: false
    });
  };

  handleBulkDeleteClick = () => {
    this.setState({
      showBulkDeleteModal: true,
      bulkDeleteMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkDelete = () => {
    this.setState({ isBulkDeleting: true });
    this.props.bulkDeleteUsers(this.state.selectedUserIds)
      .then((data) => {
        this.setState({
          isBulkDeleting: false,
          bulkDeleteMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
        this.fetchUsers();
      })
      .catch(() => {
        this.setState({
          isBulkDeleting: false,
          bulkDeleteMessage: { type: 'error', text: 'Failed to delete users. Please try again.' }
        });
      });
  };

  handleCloseBulkDeleteModal = () => {
    this.setState({
      showBulkDeleteModal: false,
      bulkDeleteMessage: { type: '', text: '' },
      isBulkDeleting: false
    });
  };

  // Bulk Change Program handlers
  handleBulkChangeProgramClick = () => {
    if (!this.props.programs || this.props.programs.length === 0) {
      this.props.getPrograms();
    }
    this.setState({
      showBulkChangeProgramModal: true,
      bulkSelectedGroupId: '',
      bulkChangeProgramMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkChangeProgram = () => {
    if (!this.state.bulkSelectedGroupId) return;
    this.setState({ isBulkChangingProgram: true });
    this.props.bulkChangeProgram(this.state.selectedUserIds, this.state.bulkSelectedGroupId)
      .then((data) => {
        this.setState({
          isBulkChangingProgram: false,
          bulkChangeProgramMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
        this.fetchUsers();
      })
      .catch(() => {
        this.setState({
          isBulkChangingProgram: false,
          bulkChangeProgramMessage: { type: 'error', text: 'Failed to change program. Please try again.' }
        });
      });
  };

  handleCloseBulkChangeProgramModal = () => {
    this.setState({
      showBulkChangeProgramModal: false,
      bulkSelectedGroupId: '',
      bulkChangeProgramMessage: { type: '', text: '' },
      isBulkChangingProgram: false
    });
  };

  // Bulk Change Pathway handlers
  handleBulkChangePathClick = () => {
    if (!this.props.programs || this.props.programs.length === 0) {
      this.props.getPrograms();
    }
    this.setState({
      showBulkChangePathModal: true,
      bulkSelectedPathId: '',
      bulkChangePathMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkChangePath = () => {
    if (!this.state.bulkSelectedPathId) return;
    this.setState({ isBulkChangingPath: true });
    this.props.bulkChangePath(this.state.selectedUserIds, this.state.bulkSelectedPathId)
      .then((data) => {
        this.setState({
          isBulkChangingPath: false,
          bulkChangePathMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
        this.fetchUsers();
      })
      .catch(() => {
        this.setState({
          isBulkChangingPath: false,
          bulkChangePathMessage: { type: 'error', text: 'Failed to change pathway. Please try again.' }
        });
      });
  };

  handleCloseBulkChangePathModal = () => {
    this.setState({
      showBulkChangePathModal: false,
      bulkSelectedPathId: '',
      bulkChangePathMessage: { type: '', text: '' },
      isBulkChangingPath: false
    });
  };

  // Adjust XP handlers
  handleAdjustXPClick = (user) => {
    this.setState({
      showAdjustXPModal: true,
      adjustXPUser: user,
      adjustXPAmount: '',
      adjustXPOperation: 'add',
      adjustXPMessage: { type: '', text: '' }
    });
  };

  handleConfirmAdjustXP = () => {
    const amount = parseInt(this.state.adjustXPAmount);
    if (!this.state.adjustXPUser || !amount || amount <= 0) return;
    this.setState({ isAdjustingXP: true });
    this.props.adjustUserXP(this.state.adjustXPUser._id, amount, this.state.adjustXPOperation)
      .then((data) => {
        this.setState({
          isAdjustingXP: false,
          adjustXPMessage: { type: 'success', text: data.msg }
        });
      })
      .catch((err) => {
        this.setState({
          isAdjustingXP: false,
          adjustXPMessage: { type: 'error', text: err?.msg || 'Failed to adjust XP.' }
        });
      });
  };

  handleCloseAdjustXPModal = () => {
    this.setState({
      showAdjustXPModal: false,
      adjustXPUser: null,
      adjustXPAmount: '',
      adjustXPOperation: 'add',
      adjustXPMessage: { type: '', text: '' },
      isAdjustingXP: false
    });
  };

  // Bulk Reset Progress handlers
  handleBulkResetProgressClick = () => {
    this.setState({
      showBulkResetProgressModal: true,
      bulkResetProgressMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkResetProgress = () => {
    this.setState({ isBulkResettingProgress: true });
    this.props.bulkResetProgress(this.state.selectedUserIds)
      .then((data) => {
        this.setState({
          isBulkResettingProgress: false,
          bulkResetProgressMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
      })
      .catch(() => {
        this.setState({
          isBulkResettingProgress: false,
          bulkResetProgressMessage: { type: 'error', text: 'Failed to reset progress. Please try again.' }
        });
      });
  };

  handleCloseBulkResetProgressModal = () => {
    this.setState({
      showBulkResetProgressModal: false,
      bulkResetProgressMessage: { type: '', text: '' },
      isBulkResettingProgress: false
    });
  };

  // Bulk Adjust XP handlers
  handleBulkAdjustXPClick = () => {
    this.setState({
      showBulkAdjustXPModal: true,
      bulkAdjustXPAmount: '',
      bulkAdjustXPOperation: 'add',
      bulkAdjustXPMessage: { type: '', text: '' }
    });
  };

  handleConfirmBulkAdjustXP = () => {
    const amount = parseInt(this.state.bulkAdjustXPAmount);
    if (!amount || amount <= 0) return;
    this.setState({ isBulkAdjustingXP: true });
    this.props.bulkAdjustXP(this.state.selectedUserIds, amount, this.state.bulkAdjustXPOperation)
      .then((data) => {
        this.setState({
          isBulkAdjustingXP: false,
          bulkAdjustXPMessage: { type: 'success', text: data.msg },
          selectedUserIds: []
        });
      })
      .catch((err) => {
        this.setState({
          isBulkAdjustingXP: false,
          bulkAdjustXPMessage: { type: 'error', text: err?.msg || 'Failed to adjust XP.' }
        });
      });
  };

  handleCloseBulkAdjustXPModal = () => {
    this.setState({
      showBulkAdjustXPModal: false,
      bulkAdjustXPAmount: '',
      bulkAdjustXPOperation: 'add',
      bulkAdjustXPMessage: { type: '', text: '' },
      isBulkAdjustingXP: false
    });
  };

  // Award Badge handlers
  handleAwardBadgeClick = (user) => {
    if (!this.props.badges || this.props.badges.length === 0) {
      this.props.getBadges();
    }
    this.setState({
      showAwardBadgeModal: true,
      awardBadgeUser: user,
      selectedBadgeId: '',
      awardBadgeMessage: { type: '', text: '' }
    });
  };

  handleConfirmAwardBadge = () => {
    if (!this.state.awardBadgeUser || !this.state.selectedBadgeId) return;
    this.setState({ isAwardingBadge: true });
    this.props.awardBadge(this.state.awardBadgeUser._id, this.state.selectedBadgeId)
      .then((data) => {
        this.setState({
          isAwardingBadge: false,
          awardBadgeMessage: { type: 'success', text: data.msg }
        });
      })
      .catch((err) => {
        this.setState({
          isAwardingBadge: false,
          awardBadgeMessage: { type: 'error', text: err?.msg || 'Failed to award badge.' }
        });
      });
  };

  handleCloseAwardBadgeModal = () => {
    this.setState({
      showAwardBadgeModal: false,
      awardBadgeUser: null,
      selectedBadgeId: '',
      awardBadgeMessage: { type: '', text: '' },
      isAwardingBadge: false
    });
  };

  toggleActionDropdown = (userId, e) => {
    if (this.state.openActionDropdown === userId) {
      this.setState({ openActionDropdown: null });
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      this.setState({
        openActionDropdown: userId,
        dropdownPosition: { top: rect.bottom + 4, left: rect.right - 192 }
      });
    }
  };

  handleCloseModal = () => {
    this.setState({
      isModalOpen: false,
      selectedUser: null,
      modalMode: 'view',
      showSuccess: false
    });
  };

  setUserData = (user) => {
    // Extract data based on exact MongoDB schema
    const name = user.name || '';
    const lastName = user.lastName || '';
    const email = user.email || '';
    const country = user.country || '';
    const role = user.role || 'visitor';
    const isActive = user.isActive !== undefined ? user.isActive : true;
    
    // Handle birthYear - support both old birthDate and new birthYear
    let birthYear = '';
    if (user.birthYear) {
      birthYear = user.birthYear;
    } else if (user.birthDate) {
      try {
        const date = new Date(user.birthDate);
        birthYear = date.getFullYear();
      } catch (e) {
        console.error('Error parsing birthDate:', e);
        birthYear = '';
      }
    }
    
    this.setState({
      name,
      lastName,
      email,
      country,
      birthYear,
      role,
      isActive,
      selectedUser: user
    });
  };

  onChange = e => {
    this.setState({ [e.target.name]: e.target.value });
  };

  onToggleChange = (name, value) => {
    this.setState({ [name]: value });
  };

  onSubmit = e => {
    e.preventDefault();
    if (this.state.modalMode === 'edit') {
      const { name, lastName, email, country, birthYear, role, isActive } = this.state;
      const userData = {
        name,
        lastName,
        email,
        country,
        birthYear: parseInt(birthYear),
        role,
        isActive
      };
      this.props.updateUser(this.state.selectedUser._id, userData);
      this.setState({ showSuccess: true });
      setTimeout(() => {
        this.setState({ showSuccess: false });
        this.handleCloseModal();
      }, 2000);
    }
  };

  formatDate(dateString) {
    if (!dateString) return 'Not specified';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  getJoinDate(user) {
    // Try different possible date field names, createdAt is the main one from MongoDB
    const joinDate = user.createdAt || user.date || user.registrationDate || user.created_at;
    if (!joinDate) return 'Not specified';
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(joinDate).toLocaleDateString(undefined, options);
  }

  render() {
    const { users, pagination } = this.props.user;
    const { 
      roleFilter, 
      statusFilter, 
      showRoleDropdown,
      showStatusDropdown,
      isModalOpen, 
      modalMode, 
      showFilters,
      currentPage,
      selectedNames,
      selectedEmails,
      selectedCountries,
      selectedUniversities,
      selectedGroups,
      startDate,
      endDate
    } = this.state;
    
    if (!users) {
      return <Spinner />;
    }

    // Users are now pre-filtered from backend, so we use them directly
    const filteredUsers = users;

    // Use filter values from API
    const { filterValues } = this.state;
    const allUserNames = filterValues.names || [];
    const allEmails = filterValues.emails || [];
    const allCountries = filterValues.countries || [];
    const allUniversities = filterValues.universities || [];
    const allGroups = filterValues.groups || [];

    // Calculate counts - use pagination totals when available, otherwise count from current users array
    const totalUsers = pagination?.total || users.length;
    
    // If we have a status filter applied, the total is accurate for that status
    // Otherwise, we need to show counts from filterValues or estimate from current page
    let activeUsersCount, deactivatedUsersCount;
    
    if (statusFilter === 'active') {
      activeUsersCount = totalUsers;
      // Can't know exact deactivated count when filtering for active only
      deactivatedUsersCount = 0;
    } else if (statusFilter === 'deactivated') {
      activeUsersCount = 0;
      deactivatedUsersCount = totalUsers;
    } else {
      // No status filter - count from current page (not ideal but best we can do)
      activeUsersCount = users.filter(u => u.isActive !== false).length;
      deactivatedUsersCount = users.filter(u => u.isActive === false).length;
    }

    const roles = [
      { value: 'all', label: 'All Roles' },
      { value: 'student', label: 'Student' },
      { value: 'instructor', label: 'Instructor' },
      { value: 'admin', label: 'Admin' },
      { value: 'visitor', label: 'Visitor' }
    ];

    const statuses = [
      { value: 'all', label: 'All Users' },
      { value: 'active', label: 'Active Users' },
      { value: 'deactivated', label: 'Deactivated Users' }
    ];

    const isDisabled = this.state.showSuccess || modalMode === 'view';
    const isEditing = modalMode === 'edit';

    const getRoleBadgeStyle = (role) => {
      switch (role) {
        case 'admin':
          return 'bg-red-100 text-red-800';
        case 'instructor':
          return 'bg-yellow-100 text-yellow-800';
        case 'student':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const hasActiveFilters = roleFilter || selectedNames.length > 0 || selectedEmails.length > 0 ||
      selectedCountries.length > 0 || selectedUniversities.length > 0 || selectedGroups.length > 0 || startDate || endDate;

    return (
      <div className="mt-8">
        <div className="flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[#054581]">Users Management</h2>
              {pagination && (
                <span className="text-sm text-gray-500">
                  ({pagination.total} total)
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const newOrder = this.state.sortOrder === 'desc' ? 'asc' : 'desc';
                  this.setState({ sortOrder: newOrder, currentPage: 1 }, () => this.fetchUsers());
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]"
              >
                <ArrowUpDown size={18} />
                <span>{this.state.sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}</span>
              </button>
              <button
                onClick={this.toggleFilters}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  hasActiveFilters
                    ? 'bg-[#1086a4] text-white shadow-md'
                    : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                }`}
              >
                <Filter size={18} />
                <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                {hasActiveFilters && (
                  <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                    Active
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mb-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#054581]">Filter Users</h3>
                <button
                  onClick={this.clearAllFilters}
                  disabled={!hasActiveFilters}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    hasActiveFilters
                      ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <X className="w-3 h-3" />
                  Clear All Filters
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Status & Role Filters Combined */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-[#1086a4]" />
                    <span className="text-sm font-medium text-[#054581]">Filter by Status & Role</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Status Dropdown */}
                    <div className="relative">
                      <label className="block text-xs text-gray-600 mb-1">Status</label>
                      <button
                        onClick={this.toggleStatusDropdown}
                        className="w-full inline-flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#1086a4] transition-colors text-sm"
                      >
                        <span className="text-sm text-gray-700 truncate">
                          {statusFilter ? statuses.find(s => s.value === statusFilter)?.label.replace(' Users', '') : 'All'}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ml-1 ${showStatusDropdown ? 'transform rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showStatusDropdown && (
                        <div className="absolute z-10 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                          <div className="py-2">
                            {statuses.map((status) => (
                              <button
                                key={status.value}
                                onClick={() => this.handleStatusSelection(status.value)}
                                className={`${
                                  (statusFilter === status.value || (!statusFilter && status.value === 'all'))
                                    ? 'bg-[#1086a4] text-white'
                                    : 'text-[#054581] hover:bg-[#f9fafb]'
                                } w-full flex items-center px-4 py-2 text-sm transition-colors`}
                              >
                                {(statusFilter === status.value || (!statusFilter && status.value === 'all')) && (
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                <span className={!(statusFilter === status.value || (!statusFilter && status.value === 'all')) ? 'ml-6' : ''}>
                                  {status.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Role Dropdown */}
                    <div className="relative">
                      <label className="block text-xs text-gray-600 mb-1">Role</label>
                      <button
                        onClick={this.toggleRoleDropdown}
                        className="w-full inline-flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#1086a4] transition-colors text-sm"
                      >
                        <span className="text-sm text-gray-700 truncate">
                          {roleFilter ? roles.find(r => r.value === roleFilter)?.label : 'All Roles'}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ml-1 ${showRoleDropdown ? 'transform rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showRoleDropdown && (
                        <div className="absolute z-10 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                          <div className="py-2">
                            {roles.map((role) => (
                              <button
                                key={role.value}
                                onClick={() => this.handleRoleSelection(role.value)}
                                className={`${
                                  (roleFilter === role.value || (!roleFilter && role.value === 'all'))
                                    ? 'bg-[#1086a4] text-white'
                                    : 'text-[#054581] hover:bg-[#f9fafb]'
                                } w-full flex items-center px-4 py-2 text-sm transition-colors`}
                              >
                                {(roleFilter === role.value || (!roleFilter && role.value === 'all')) && (
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                <span className={!(roleFilter === role.value || (!roleFilter && role.value === 'all')) ? 'ml-6' : ''}>
                                  {role.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#1086a4]" />
                    <span className="text-sm font-medium text-[#054581]">Join Date Range</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">From</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => this.setState({ startDate: e.target.value, currentPage: 1 }, this.handleFilterChange)}
                        className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">To</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => this.setState({ endDate: e.target.value, currentPage: 1 }, this.handleFilterChange)}
                        className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Multi-select Filters */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <MultiSelectSearchFilter
                  items={allUserNames.map(name => ({ _id: name, title: name }))}
                  selectedItems={selectedNames}
                  onSelectionChange={(selected) => this.setState({ selectedNames: selected, currentPage: 1 }, this.handleFilterChange)}
                  title="Names"
                  placeholder="Search names..."
                  emptyMessage="No names available"
                />

                <MultiSelectSearchFilter
                  items={allEmails.map(email => ({ _id: email, title: email }))}
                  selectedItems={selectedEmails}
                  onSelectionChange={(selected) => this.setState({ selectedEmails: selected, currentPage: 1 }, this.handleFilterChange)}
                  title="Emails"
                  placeholder="Search emails..."
                  emptyMessage="No emails available"
                />

                <MultiSelectSearchFilter
                  items={allCountries.map(country => ({ _id: country, title: country }))}
                  selectedItems={selectedCountries}
                  onSelectionChange={(selected) => this.setState({ selectedCountries: selected, currentPage: 1 }, this.handleFilterChange)}
                  title="Countries"
                  placeholder="Search countries..."
                  emptyMessage="No countries available"
                />

                <MultiSelectSearchFilter
                  items={allUniversities.map(university => ({ _id: university, title: university }))}
                  selectedItems={selectedUniversities}
                  onSelectionChange={(selected) => this.setState({ selectedUniversities: selected, currentPage: 1 }, this.handleFilterChange)}
                  title="Universities"
                  placeholder="Search universities..."
                  emptyMessage="No universities available"
                />

                {allGroups.length > 0 && (
                  <MultiSelectSearchFilter
                    items={allGroups}
                    selectedItems={selectedGroups}
                    onSelectionChange={(selected) => this.setState({ selectedGroups: selected, currentPage: 1 }, this.handleFilterChange)}
                    title="Groups"
                    placeholder="Search groups..."
                    emptyMessage="No groups available"
                  />
                )}
              </div>
            </div>
          )}

          {/* Bulk Action Buttons */}
          {this.state.selectedUserIds.length > 0 && (
            <div className="mb-4 flex items-center gap-2 relative">
              <span className="text-xs text-gray-500 mr-1">{this.state.selectedUserIds.length} selected</span>
              <button
                onClick={() => this.setState(prev => ({ showBulkActionDropdown: !prev.showBulkActionDropdown }))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#054581] text-white text-xs font-medium hover:bg-[#043a6e] transition-all"
              >
                Bulk Actions
                <ChevronDown size={14} />
              </button>
              {this.state.showBulkActionDropdown && (
                <div className="absolute top-full left-[70px] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] py-1">
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkActivateClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                  >
                    <UserCheck size={14} />
                    Activate
                  </button>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkDeactivateClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    <X size={14} />
                    Deactivate
                  </button>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkChangeProgramClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                  >
                    <ArrowRightLeft size={14} />
                    Change Program
                  </button>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkChangePathClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                  >
                    <GitBranch size={14} />
                    Change Pathway
                  </button>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkResetProgressClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors"
                  >
                    <RotateCcw size={14} />
                    Reset Progress
                  </button>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkAdjustXPClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <Zap size={14} />
                    Adjust XP
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => { this.setState({ showBulkActionDropdown: false }); this.handleBulkDeleteClick(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Pagination - Top */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => this.handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    pagination.hasPrevPage
                      ? 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <span className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => this.handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    pagination.hasNextPage
                      ? 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Mobile Card View */}
          <div className="block md:hidden">
            {/* Mobile Select All */}
            <div className="mb-3 flex items-center gap-3 px-1">
              <input
                type="checkbox"
                checked={(() => {
                  const selectableUsers = (filteredUsers || []).filter(u => u.role !== 'platform_admin' && (u.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && u._id !== this.props.currentUser?._id);
                  return selectableUsers.length > 0 && selectableUsers.every(u => this.state.selectedUserIds.includes(u._id));
                })()}
                onChange={this.handleSelectAll}
                className="w-4 h-4 text-[#1086a4] border-gray-300 rounded focus:ring-[#1086a4] cursor-pointer"
              />
              <span className="text-sm text-gray-600">Select All</span>
            </div>
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div key={user._id} className={`bg-white rounded-lg shadow-sm border ${this.state.selectedUserIds.includes(user._id) ? 'border-[#1086a4] bg-blue-50' : 'border-gray-200'}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && user._id !== this.props.currentUser?._id ? (
                        <input
                          type="checkbox"
                          checked={this.state.selectedUserIds.includes(user._id)}
                          onChange={() => this.handleSelectUser(user._id)}
                          className="w-4 h-4 mt-3 text-[#1086a4] border-gray-300 rounded focus:ring-[#1086a4] cursor-pointer flex-shrink-0"
                        />
                      ) : (
                        <input type="checkbox" disabled className="w-4 h-4 mt-3 border-gray-200 rounded opacity-30 cursor-not-allowed flex-shrink-0" />
                      )}
                      {/* Icon/Avatar */}
                      <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#1086a4] font-medium text-lg">
                          {`${user.name?.[0] || ''}${user.lastName?.[0] || ''}`?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#054581] mb-2">
                          {user.name} {user.lastName}
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-[#1086a4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="text-[#1e2d40] font-medium truncate">{user.email}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <svg className="w-4 h-4 text-[#1086a4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-[#1e2d40]">{this.getJoinDate(user)}</span>
                            </div>
                            <div className="flex gap-2">
                              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getRoleBadgeStyle(user.role)}`}>
                                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                              </span>
                              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                                user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {user.isActive !== false ? 'Active' : 'Deactivated'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end">
                    <div className="relative">
                      <button
                        onClick={(e) => this.toggleActionDropdown(user._id, e)}
                        className="p-2 text-gray-500 hover:text-[#054581] hover:bg-white rounded-lg transition-colors"
                      >
                        <MoreVertical size={20} />
                      </button>
                      {this.state.openActionDropdown === user._id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => this.setState({ openActionDropdown: null })} />
                          <div className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1" style={{ top: this.state.dropdownPosition.top, left: this.state.dropdownPosition.left }}>
                            {((this.props.currentUser?.role === 'admin' || this.props.currentUser?.role === 'platform_admin') || this.props.currentUser?.role === 'instructor') && (
                              <button
                                onClick={() => { this.setState({ openActionDropdown: null }); this.handleViewProfileClick(user); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <UserRound className="w-4 h-4 text-purple-600" />
                                View Profile
                              </button>
                            )}
                            <button
                              onClick={() => { this.setState({ openActionDropdown: null }); this.handleViewClick(user); }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Eye className="w-4 h-4 text-[#1086a4]" />
                              View Details
                            </button>
                            {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && (
                              <button
                                onClick={() => { this.setState({ openActionDropdown: null }); this.handleEditClick(user); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Pencil className="w-4 h-4 text-[#1086a4]" />
                                Edit User
                              </button>
                            )}
                            {(this.props.currentUser?.role === 'admin' || this.props.currentUser?.role === 'platform_admin') && user._id !== this.props.currentUser._id && (
                              <>
                                <button
                                  onClick={() => { this.setState({ openActionDropdown: null }); this.handleImpersonateClick(user); }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                  Impersonate
                                </button>
                                {user.role === 'student' && (
                                  <>
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleChangeProgramClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <ArrowRightLeft className="w-4 h-4 text-orange-600" />
                                      Change Program
                                    </button>
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleResetProgressClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <RotateCcw className="w-4 h-4 text-red-600" />
                                      Reset Progress
                                    </button>
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleAwardBadgeClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <Award className="w-4 h-4 text-yellow-600" />
                                      Award Badge
                                    </button>
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleAdjustXPClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <Zap className="w-4 h-4 text-amber-500" />
                                      Adjust XP
                                    </button>
                                  </>
                                )}
                                {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && (
                                  <>
                                    <div className="border-t border-gray-100 my-1" />
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleDeleteClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete User
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div 
            ref={this.tableRef}
            className="hidden md:block w-full overflow-x-auto rounded-lg border border-gray-200 cursor-grab active:cursor-grabbing"
            onMouseDown={this.handleMouseDown}
            onMouseLeave={this.handleMouseLeave}
            onMouseUp={this.handleMouseUp}
            onMouseMove={this.handleMouseMove}
          >
            <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 w-10 cursor-auto">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const selectableUsers = (filteredUsers || []).filter(u => u.role !== 'platform_admin' && (u.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && u._id !== this.props.currentUser?._id);
                            return selectableUsers.length > 0 && selectableUsers.every(u => this.state.selectedUserIds.includes(u._id));
                          })()}
                          onChange={this.handleSelectAll}
                          className="w-4 h-4 text-[#1086a4] border-gray-300 rounded focus:ring-[#1086a4] cursor-pointer"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-40">
                        Full Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-52">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-48">
                        Program
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-40">
                        University
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-28">
                        Role
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-28">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider w-32">
                        Join Date
                      </th>
                      <th scope="col" className="relative px-6 py-3 w-24 cursor-auto">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user._id} className={`hover:bg-gray-50 transition-colors duration-150 ${this.state.selectedUserIds.includes(user._id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap cursor-auto">
                          {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && user._id !== this.props.currentUser?._id ? (
                            <input
                              type="checkbox"
                              checked={this.state.selectedUserIds.includes(user._id)}
                              onChange={() => this.handleSelectUser(user._id)}
                              className="w-4 h-4 text-[#1086a4] border-gray-300 rounded focus:ring-[#1086a4] cursor-pointer"
                            />
                          ) : (
                            <input type="checkbox" disabled className="w-4 h-4 border-gray-200 rounded opacity-30 cursor-not-allowed" />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name} {user.lastName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.group?.program?.title || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.university || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.role === 'platform_admin'
                              ? 'bg-purple-100 text-purple-800'
                              : user.role === 'admin'
                              ? 'bg-red-100 text-red-800'
                              : user.role === 'instructor'
                              ? 'bg-orange-100 text-orange-800'
                              : user.role === 'student'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role === 'platform_admin' ? 'Platform Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive !== false ? 'Active' : 'Deactivated'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {this.getJoinDate(user)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium cursor-auto">
                          <div className="relative">
                            <button
                              onClick={(e) => this.toggleActionDropdown(user._id, e)}
                              className="p-2 text-gray-500 hover:text-[#054581] hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </button>
                            {this.state.openActionDropdown === user._id && (
                              <>
                                <div className="fixed inset-0 z-40" onClick={() => this.setState({ openActionDropdown: null })} />
                                <div className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1" style={{ top: this.state.dropdownPosition.top, left: this.state.dropdownPosition.left }}>
                                  {((this.props.currentUser?.role === 'admin' || this.props.currentUser?.role === 'platform_admin') || this.props.currentUser?.role === 'instructor') && (
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleViewProfileClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <UserRound className="w-4 h-4 text-purple-600" />
                                      View Profile
                                    </button>
                                  )}
                                  <button
                                    onClick={() => { this.setState({ openActionDropdown: null }); this.handleViewClick(user); }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Eye className="w-4 h-4 text-[#1086a4]" />
                                    View Details
                                  </button>
                                  {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && (
                                    <button
                                      onClick={() => { this.setState({ openActionDropdown: null }); this.handleEditClick(user); }}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <Pencil className="w-4 h-4 text-[#1086a4]" />
                                      Edit User
                                    </button>
                                  )}
                                  {(this.props.currentUser?.role === 'admin' || this.props.currentUser?.role === 'platform_admin') && user._id !== this.props.currentUser._id && (
                                    <>
                                      <button
                                        onClick={() => { this.setState({ openActionDropdown: null }); this.handleImpersonateClick(user); }}
                                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                      >
                                        <UserCheck className="w-4 h-4 text-green-600" />
                                        Impersonate
                                      </button>
                                      {user.role === 'student' && (
                                        <>
                                          <button
                                            onClick={() => { this.setState({ openActionDropdown: null }); this.handleChangeProgramClick(user); }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                          >
                                            <ArrowRightLeft className="w-4 h-4 text-orange-600" />
                                            Change Program
                                          </button>
                                          <button
                                            onClick={() => { this.setState({ openActionDropdown: null }); this.handleResetProgressClick(user); }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                          >
                                            <RotateCcw className="w-4 h-4 text-red-600" />
                                            Reset Progress
                                          </button>
                                          <button
                                            onClick={() => { this.setState({ openActionDropdown: null }); this.handleAwardBadgeClick(user); }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                          >
                                            <Award className="w-4 h-4 text-yellow-600" />
                                            Award Badge
                                          </button>
                                          <button
                                            onClick={() => { this.setState({ openActionDropdown: null }); this.handleAdjustXPClick(user); }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                          >
                                            <Zap className="w-4 h-4 text-amber-500" />
                                            Adjust XP
                                          </button>
                                        </>
                                      )}
                                      {user.role !== 'platform_admin' && (user.role !== 'admin' || this.props.currentUser?.role === 'platform_admin') && (
                                        <>
                                          <div className="border-t border-gray-100 my-1" />
                                          <button
                                            onClick={() => { this.setState({ openActionDropdown: null }); this.handleDeleteClick(user); }}
                                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                            Delete User
                                          </button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
            </table>
          </div>

          {/* Pagination - Bottom */}
          {pagination && (
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} users
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Show</span>
                  <select
                    value={this.state.limit}
                    onChange={(e) => this.setState({ limit: parseInt(e.target.value), currentPage: 1, selectedUserIds: [] }, this.fetchUsers)}
                    className="px-2 py-1 text-xs text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>
              {pagination.totalPages > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      pagination.hasPrevPage
                        ? 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <span className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700">
                    Page {currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      pagination.hasNextPage
                        ? 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal - Made more mobile friendly */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg w-full max-w-md mx-auto p-6 border-2 border-[#1086a4]">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#054581]">
                  {isEditing ? 'Edit User' : 'View User'}
                </h3>
                <button 
                  onClick={this.handleCloseModal} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                  disabled={this.state.showSuccess}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Success message */}
              {this.state.showSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-400 text-green-700 rounded">
                  User updated successfully!
                </div>
              )}

              {isEditing ? (
                /* Edit Form - Made more mobile friendly */
                <form onSubmit={this.onSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#054581] mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={this.state.name}
                        onChange={this.onChange}
                        className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                        required
                        disabled={this.state.showSuccess}
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-[#054581] mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={this.state.lastName}
                        onChange={this.onChange}
                        className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                        required
                        disabled={this.state.showSuccess}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#054581] mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={this.state.email}
                      onChange={this.onChange}
                      className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      required
                      disabled={this.state.showSuccess}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-[#054581] mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        id="country"
                        value={this.state.country}
                        onChange={this.onChange}
                        className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                        required
                        disabled={this.state.showSuccess}
                      />
                    </div>

                    <div>
                      <label htmlFor="birthYear" className="block text-sm font-medium text-[#054581] mb-2">
                        Birth Year
                      </label>
                      <select
                        name="birthYear"
                        id="birthYear"
                        value={this.state.birthYear}
                        onChange={this.onChange}
                        className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                        required
                        disabled={this.state.showSuccess}
                      >
                        <option value="">Select year</option>
                        {Array.from({ length: 121 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-[#054581] mb-2">
                      Role
                    </label>
                    <select
                      name="role"
                      id="role"
                      value={this.state.role}
                      onChange={this.onChange}
                      className="w-full px-3 py-2.5 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      required
                      disabled={this.state.showSuccess}
                    >
                      <option value="">Select a role</option>
                      <option value="visitor">Visitor</option>
                      <option value="student">Student</option>
                      <option value="instructor">Instructor</option>
                      <option value="admin">Admin</option>
                      {this.props.currentUser?.role === 'platform_admin' && (
                        <option value="platform_admin">Platform Admin</option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#054581] mb-2">
                      Status
                    </label>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => this.onToggleChange('isActive', !this.state.isActive)}
                        className={`${
                          this.state.isActive ? 'bg-[#1086a4]' : 'bg-gray-300'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 ${
                          this.state.selectedUser && (this.state.selectedUser.role === 'platform_admin' || (this.state.selectedUser.role === 'admin' && this.props.currentUser?.role !== 'platform_admin')) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={this.state.showSuccess || (this.state.selectedUser && (this.state.selectedUser.role === 'platform_admin' || (this.state.selectedUser.role === 'admin' && this.props.currentUser?.role !== 'platform_admin')))}
                      >
                        <span
                          className={`${
                            this.state.isActive ? 'translate-x-5' : 'translate-x-0'
                          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                      <span className="ml-3 text-sm">
                        <span className={`font-medium ${this.state.isActive ? 'text-green-700' : 'text-red-700'}`}>
                          {this.state.isActive ? 'Active' : 'Deactivated'}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      type="button"
                      onClick={this.handleCloseModal}
                      className="w-full sm:flex-1 py-2.5 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                      disabled={this.state.showSuccess}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="w-full sm:flex-1 py-2.5 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                      disabled={this.state.showSuccess}
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                /* View Mode - Made more mobile friendly */
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Name</label>
                      <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4]">
                        {this.state.name} {this.state.lastName}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Email</label>
                      <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4] break-all">
                        {this.state.email}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Country</label>
                      <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4]">
                        {this.state.country || 'Not specified'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Birth Year</label>
                      <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4]">
                        {this.state.birthYear || 'Not specified'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Role</label>
                      <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4] capitalize">
                        {this.state.role}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Status</label>
                      <div className="p-2.5 bg-[#f9fafb] rounded border border-[#1086a4]">
                        <span className={`font-medium ${this.state.isActive ? 'text-green-700' : 'text-red-700'}`}>
                          {this.state.isActive ? 'Active' : 'Deactivated'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#054581] mb-2">Joined</label>
                    <div className="text-gray-900 p-2.5 bg-[#f9fafb] rounded border border-[#1086a4]">
                      {this.state.selectedUser ? this.getJoinDate(this.state.selectedUser) : 'Not specified'}
                    </div>
                  </div>

                  {/* Close Button */}
                  <div className="mt-6">
                    <button
                      onClick={this.handleCloseModal}
                      className="w-full py-2.5 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reset Progress Confirmation Modal */}
        {this.state.showResetModal && this.state.resetUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4">Reset User Progress</h3>
              
              {/* Success/Error Messages */}
              {this.state.resetMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.resetMessage.text}
                </div>
              )}
              {this.state.resetMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.resetMessage.text}
                </div>
              )}
              
              {!this.state.resetMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to reset all progress and XP points for <strong>{this.state.resetUser.name} {this.state.resetUser.lastName}</strong>?
                  </p>
                  <p className="text-gray-700 mb-6">
                    This action will permanently delete all their progress including completed lessons, course positions, onboarding status, <strong>and all experience points</strong>. This action cannot be undone.
                  </p>
                </>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseResetModal}
                  disabled={this.state.isResetting}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isResetting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.resetMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.resetMessage.type && (
                  <button
                    onClick={this.handleConfirmResetProgress}
                    disabled={this.state.isResetting}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isResetting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {this.state.isResetting ? 'Resetting...' : 'Reset Progress'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Delete User Modal */}
        {this.state.showDeleteModal && this.state.deleteUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4">Delete User</h3>
              
              {/* Success/Error Messages */}
              {this.state.deleteMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.deleteMessage.text}
                </div>
              )}
              {this.state.deleteMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.deleteMessage.text}
                </div>
              )}
              
              {!this.state.deleteMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to delete <strong>{this.state.deleteUser.name} {this.state.deleteUser.lastName}</strong>?
                  </p>
                  <p className="text-gray-700 mb-6">
                    This action will permanently remove the user account, all their data, progress, and access to the system. <strong>This action cannot be undone.</strong>
                  </p>
                </>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseDeleteModal}
                  disabled={this.state.isDeleting}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isDeleting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.deleteMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.deleteMessage.type && (
                  <button
                    onClick={this.handleConfirmDeleteUser}
                    disabled={this.state.isDeleting}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isDeleting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {this.state.isDeleting ? 'Deleting...' : 'Delete User'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Impersonate Confirmation Modal */}
        {this.state.showImpersonateModal && this.state.impersonateUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Impersonate User</h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to impersonate <strong>{this.state.impersonateUser.name} {this.state.impersonateUser.lastName}</strong>? You will be logged in as this user temporarily.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseImpersonateModal}
                  className="px-4 py-2 rounded-lg font-semibold bg-gray-300 hover:bg-gray-400 text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleConfirmImpersonate}
                  className="px-4 py-2 rounded-lg font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-colors"
                >
                  Impersonate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Program Modal */}
        {this.state.showChangeProgramModal && this.state.changeProgramUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-orange-700 mb-4">Change Program</h3>

              {this.state.changeProgramMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.changeProgramMessage.text}
                </div>
              )}
              {this.state.changeProgramMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.changeProgramMessage.text}
                </div>
              )}

              {!this.state.changeProgramMessage.type && (() => {
                // Find the selected program based on the selected group
                const selectedProgram = this.state.selectedGroupId
                  ? (this.props.programs || []).find(p => (p.groups || []).some(g => String(g._id) === String(this.state.selectedGroupId)))
                  : null;
                const availablePaths = selectedProgram?.paths || [];

                return (
                  <>
                    <p className="text-gray-700 mb-2">
                      Change the program and pathway for <strong>{this.state.changeProgramUser.name} {this.state.changeProgramUser.lastName}</strong>.
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-red-700 text-sm font-medium">Warning: This will reset ALL user data including XP, progress, and submissions. Only Name, Email, and Password will be retained.</p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#054581] mb-2">Select New Program & Group</label>
                      <select
                        value={this.state.selectedGroupId}
                        onChange={(e) => this.setState({ selectedGroupId: e.target.value, selectedPathId: '' })}
                        className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      >
                        <option value="">Select a group...</option>
                        {(this.props.programs || []).map(program => (
                          (program.groups || []).map(group => (
                            <option key={group._id} value={group._id}>
                              {program.title} — {group.title}
                            </option>
                          ))
                        ))}
                      </select>
                    </div>

                    {this.state.selectedGroupId && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-[#054581] mb-2">Select Pathway (Optional)</label>
                        {availablePaths.length > 0 ? (
                          <select
                            value={this.state.selectedPathId}
                            onChange={(e) => this.setState({ selectedPathId: e.target.value })}
                            className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                          >
                            <option value="">Skip — user will choose during onboarding</option>
                            {availablePaths.map(path => (
                              <option key={path._id} value={path._id}>
                                {path.title}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-gray-500 text-sm">No pathways available for this program.</p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseChangeProgramModal}
                  disabled={this.state.isChangingProgram}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isChangingProgram ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.changeProgramMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.changeProgramMessage.type && (
                  <button
                    onClick={this.handleConfirmChangeProgram}
                    disabled={this.state.isChangingProgram || !this.state.selectedGroupId}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isChangingProgram || !this.state.selectedGroupId
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {this.state.isChangingProgram ? 'Changing...' : 'Confirm Change'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Deactivate Modal */}
        {this.state.showBulkDeactivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4">Deactivate Selected Users</h3>

              {this.state.bulkDeactivateMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkDeactivateMessage.text}
                </div>
              )}
              {this.state.bulkDeactivateMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkDeactivateMessage.text}
                </div>
              )}

              {!this.state.bulkDeactivateMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to deactivate <strong>{this.state.selectedUserIds.length}</strong> selected user(s)?
                  </p>
                  <p className="text-gray-700 mb-6">
                    Deactivated users will no longer be able to log in. This action can be reversed by reactivating individual users later.
                  </p>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkDeactivateModal}
                  disabled={this.state.isBulkDeactivating}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkDeactivating ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkDeactivateMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkDeactivateMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkDeactivate}
                    disabled={this.state.isBulkDeactivating}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkDeactivating ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {this.state.isBulkDeactivating ? 'Deactivating...' : 'Deactivate All'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Modal */}
        {this.state.showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4">Delete Selected Users</h3>

              {this.state.bulkDeleteMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkDeleteMessage.text}
                </div>
              )}
              {this.state.bulkDeleteMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkDeleteMessage.text}
                </div>
              )}

              {!this.state.bulkDeleteMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to permanently delete <strong>{this.state.selectedUserIds.length}</strong> selected user(s)?
                  </p>
                  <p className="text-red-600 font-medium mb-6">
                    This will remove all their data, progress, and submissions. This action cannot be undone.
                  </p>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkDeleteModal}
                  disabled={this.state.isBulkDeleting}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkDeleting ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkDeleteMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkDeleteMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkDelete}
                    disabled={this.state.isBulkDeleting}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkDeleting ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {this.state.isBulkDeleting ? 'Deleting...' : 'Delete All'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Activate Modal */}
        {this.state.showBulkActivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-green-700 mb-4">Activate Selected Users</h3>

              {this.state.bulkActivateMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkActivateMessage.text}
                </div>
              )}
              {this.state.bulkActivateMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkActivateMessage.text}
                </div>
              )}

              {!this.state.bulkActivateMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to activate <strong>{this.state.selectedUserIds.length}</strong> selected user(s)?
                  </p>
                  <p className="text-gray-700 mb-6">
                    Activated users will be able to log in and access the platform again.
                  </p>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkActivateModal}
                  disabled={this.state.isBulkActivating}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkActivating ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkActivateMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkActivateMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkActivate}
                    disabled={this.state.isBulkActivating}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkActivating ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {this.state.isBulkActivating ? 'Activating...' : 'Activate All'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Change Pathway Modal */}
        {/* Change Pathway modal removed — combined into Change Program modal above */}

        {/* Bulk Change Program Modal */}
        {this.state.showBulkChangeProgramModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-orange-700 mb-4">Change Program for Selected Users</h3>

              {this.state.bulkChangeProgramMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkChangeProgramMessage.text}
                </div>
              )}
              {this.state.bulkChangeProgramMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkChangeProgramMessage.text}
                </div>
              )}

              {!this.state.bulkChangeProgramMessage.type && (
                <>
                  <p className="text-gray-700 mb-2">
                    Change the program for <strong>{this.state.selectedUserIds.length}</strong> selected user(s).
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-700 text-sm font-medium">Warning: This will reset ALL user data including XP, progress, and submissions. Only Name, Email, and Password will be retained. Non-student users will be skipped.</p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Select New Program & Group</label>
                    <select
                      value={this.state.bulkSelectedGroupId}
                      onChange={(e) => this.setState({ bulkSelectedGroupId: e.target.value })}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                    >
                      <option value="">Select a group...</option>
                      {(this.props.programs || []).map(program => (
                        (program.groups || []).map(group => (
                          <option key={group._id} value={group._id}>
                            {program.title} — {group.title}
                          </option>
                        ))
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkChangeProgramModal}
                  disabled={this.state.isBulkChangingProgram}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkChangingProgram ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkChangeProgramMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkChangeProgramMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkChangeProgram}
                    disabled={this.state.isBulkChangingProgram || !this.state.bulkSelectedGroupId}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkChangingProgram || !this.state.bulkSelectedGroupId
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {this.state.isBulkChangingProgram ? 'Changing...' : 'Change Program'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Change Pathway Modal */}
        {this.state.showBulkChangePathModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-indigo-700 mb-4">Change Pathway for Selected Users</h3>

              {this.state.bulkChangePathMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkChangePathMessage.text}
                </div>
              )}
              {this.state.bulkChangePathMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkChangePathMessage.text}
                </div>
              )}

              {!this.state.bulkChangePathMessage.type && (() => {
                // Collect all unique paths across all programs
                const allPaths = [];
                const seenPathIds = new Set();
                (this.props.programs || []).forEach(program => {
                  (program.paths || []).forEach(path => {
                    if (!seenPathIds.has(path._id)) {
                      seenPathIds.add(path._id);
                      allPaths.push({ ...path, programTitle: program.title });
                    }
                  });
                });

                return (
                  <>
                    <p className="text-gray-700 mb-2">
                      Change the pathway for <strong>{this.state.selectedUserIds.length}</strong> selected user(s).
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-blue-700 text-sm">Progress will be reset to the start of the new pathway. XP and submissions will be retained. Users whose program doesn't include the selected pathway will be skipped.</p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#054581] mb-2">Select New Pathway</label>
                      <select
                        value={this.state.bulkSelectedPathId}
                        onChange={(e) => this.setState({ bulkSelectedPathId: e.target.value })}
                        className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      >
                        <option value="">Select a pathway...</option>
                        {allPaths.map(path => (
                          <option key={path._id} value={path._id}>
                            {path.title} ({path.programTitle})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                );
              })()}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkChangePathModal}
                  disabled={this.state.isBulkChangingPath}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkChangingPath ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkChangePathMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkChangePathMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkChangePath}
                    disabled={this.state.isBulkChangingPath || !this.state.bulkSelectedPathId}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkChangingPath || !this.state.bulkSelectedPathId
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {this.state.isBulkChangingPath ? 'Changing...' : 'Change Pathway'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Award Badge Modal */}
        {this.state.showAwardBadgeModal && this.state.awardBadgeUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-yellow-700 mb-4">Award Badge</h3>

              {this.state.awardBadgeMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.awardBadgeMessage.text}
                </div>
              )}
              {this.state.awardBadgeMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.awardBadgeMessage.text}
                </div>
              )}

              {!this.state.awardBadgeMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Award a badge to <strong>{this.state.awardBadgeUser.name} {this.state.awardBadgeUser.lastName}</strong>.
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Select Badge</label>
                    <select
                      value={this.state.selectedBadgeId}
                      onChange={(e) => this.setState({ selectedBadgeId: e.target.value })}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                    >
                      <option value="">Select a badge...</option>
                      {(this.props.badges || []).map(badge => (
                        <option key={badge._id} value={badge._id}>
                          {badge.title} ({badge.rarity})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseAwardBadgeModal}
                  disabled={this.state.isAwardingBadge}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isAwardingBadge ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.awardBadgeMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.awardBadgeMessage.type && (
                  <button
                    onClick={this.handleConfirmAwardBadge}
                    disabled={this.state.isAwardingBadge || !this.state.selectedBadgeId}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isAwardingBadge || !this.state.selectedBadgeId
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    }`}
                  >
                    {this.state.isAwardingBadge ? 'Awarding...' : 'Award Badge'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Adjust XP Modal */}
        {this.state.showAdjustXPModal && this.state.adjustXPUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-amber-700 mb-4">Adjust XP</h3>

              {this.state.adjustXPMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.adjustXPMessage.text}
                </div>
              )}
              {this.state.adjustXPMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.adjustXPMessage.text}
                </div>
              )}

              {!this.state.adjustXPMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Adjust XP for <strong>{this.state.adjustXPUser.name} {this.state.adjustXPUser.lastName}</strong>.
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Operation</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => this.setState({ adjustXPOperation: 'add' })}
                        className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          this.state.adjustXPOperation === 'add'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        + Add
                      </button>
                      <button
                        type="button"
                        onClick={() => this.setState({ adjustXPOperation: 'subtract' })}
                        className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          this.state.adjustXPOperation === 'subtract'
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        - Subtract
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Amount</label>
                    <input
                      type="number"
                      min="1"
                      max="10000"
                      value={this.state.adjustXPAmount}
                      onChange={(e) => this.setState({ adjustXPAmount: e.target.value })}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      placeholder="Enter XP amount"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseAdjustXPModal}
                  disabled={this.state.isAdjustingXP}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isAdjustingXP ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.adjustXPMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.adjustXPMessage.type && (
                  <button
                    onClick={this.handleConfirmAdjustXP}
                    disabled={this.state.isAdjustingXP || !this.state.adjustXPAmount || parseInt(this.state.adjustXPAmount) <= 0}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isAdjustingXP || !this.state.adjustXPAmount || parseInt(this.state.adjustXPAmount) <= 0
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                  >
                    {this.state.isAdjustingXP ? 'Adjusting...' : `${this.state.adjustXPOperation === 'add' ? 'Add' : 'Subtract'} XP`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Reset Progress Modal */}
        {this.state.showBulkResetProgressModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-yellow-700 mb-4">Reset Progress for Selected Users</h3>

              {this.state.bulkResetProgressMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkResetProgressMessage.text}
                </div>
              )}
              {this.state.bulkResetProgressMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkResetProgressMessage.text}
                </div>
              )}

              {!this.state.bulkResetProgressMessage.type && (
                <>
                  <p className="text-gray-700 mb-2">
                    Are you sure you want to reset progress for <strong>{this.state.selectedUserIds.length}</strong> selected user(s)?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-700 text-sm font-medium">Warning: This will reset ALL progress, submissions, XP, and onboarding status. Non-student users will be skipped.</p>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkResetProgressModal}
                  disabled={this.state.isBulkResettingProgress}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkResettingProgress ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkResetProgressMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkResetProgressMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkResetProgress}
                    disabled={this.state.isBulkResettingProgress}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkResettingProgress ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {this.state.isBulkResettingProgress ? 'Resetting...' : 'Reset All'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Adjust XP Modal */}
        {this.state.showBulkAdjustXPModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-amber-700 mb-4">Adjust XP for Selected Users</h3>

              {this.state.bulkAdjustXPMessage.type === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-lg">
                  {this.state.bulkAdjustXPMessage.text}
                </div>
              )}
              {this.state.bulkAdjustXPMessage.type === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-500 text-red-700 rounded-lg">
                  {this.state.bulkAdjustXPMessage.text}
                </div>
              )}

              {!this.state.bulkAdjustXPMessage.type && (
                <>
                  <p className="text-gray-700 mb-4">
                    Adjust XP for <strong>{this.state.selectedUserIds.length}</strong> selected user(s).
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Operation</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => this.setState({ bulkAdjustXPOperation: 'add' })}
                        className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          this.state.bulkAdjustXPOperation === 'add'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        + Add
                      </button>
                      <button
                        type="button"
                        onClick={() => this.setState({ bulkAdjustXPOperation: 'subtract' })}
                        className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          this.state.bulkAdjustXPOperation === 'subtract'
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        - Subtract
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Amount</label>
                    <input
                      type="number"
                      min="1"
                      max="10000"
                      value={this.state.bulkAdjustXPAmount}
                      onChange={(e) => this.setState({ bulkAdjustXPAmount: e.target.value })}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      placeholder="Enter XP amount"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-blue-700 text-sm">XP cannot go below 0. If subtracting more than a user's current XP, it will be set to 0.</p>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={this.handleCloseBulkAdjustXPModal}
                  disabled={this.state.isBulkAdjustingXP}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    this.state.isBulkAdjustingXP ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  {this.state.bulkAdjustXPMessage.type ? 'Close' : 'Cancel'}
                </button>
                {!this.state.bulkAdjustXPMessage.type && (
                  <button
                    onClick={this.handleConfirmBulkAdjustXP}
                    disabled={this.state.isBulkAdjustingXP || !this.state.bulkAdjustXPAmount || parseInt(this.state.bulkAdjustXPAmount) <= 0}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      this.state.isBulkAdjustingXP || !this.state.bulkAdjustXPAmount || parseInt(this.state.bulkAdjustXPAmount) <= 0
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                  >
                    {this.state.isBulkAdjustingXP ? 'Adjusting...' : `${this.state.bulkAdjustXPOperation === 'add' ? 'Add' : 'Subtract'} XP`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.auth.user,
  programs: state.program.programs,
  badges: state.badge.badges
});

export default withRouter(connect(
  mapStateToProps,
  { getUsers, updateUser, getUser, deleteUser, getUserFilterValues, startImpersonation, resetUserProgress, changeUserProgram, changeUserPath, getPrograms, bulkDeactivateUsers, bulkActivateUsers, bulkDeleteUsers, bulkChangeProgram, bulkChangePath, getBadges, awardBadge, adjustUserXP, bulkAdjustXP, bulkResetProgress }
)(ManageUsersTab));
